import { PayMethod, PayMethodType } from './../payment/payment'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { combineLatest, Observable, Subject } from 'rxjs'
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core'
import { Store } from '@ngrx/store'
import { Sale, StatusCode } from '../sale'
import { saleSelectors } from '../sale.selectors'
import { CollectStatusType, Payment } from '../payment/payment'
import { paymentSelectors } from '../payment/payment.selectors'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { PaymentCreateModalComponent } from '../payment/create-modal/show.component'
import { paymentActions } from '../payment/payment.actions'
import { BulkActionEvent, BulkAction } from 'src/app/shared/interfaces'
import { LoadingModalService } from 'src/app/shared/services/loading-modal.service'
import {
  configOptionsSelector,
  configurationOptionSelectors
} from '../../configuration-option/configuration-option.selectors'
import { ConfigOptionsKeys } from 'src/app/shared/constants'
import { saleActions } from '../sale.actions'
import { SalePaymentModalComponent } from './show-modal/show.component'

@UntilDestroy()
@Component({
  selector: 'sale-payment-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SalePaymentTabComponent implements OnInit {
  @Input() saleId: number
  @Input() readonly: boolean = false

  public sale$: Observable<Sale>
  public payments$: Observable<Payment[]>
  public modalValues$ = new Subject<Payment>()
  public bsModalRef: BsModalRef
  public canSetToPayInFrontend$: Observable<boolean>
  public canCreatePayment: boolean = false
  public accessRights = AccessRights
  public prePaymentFactorActive: boolean = false
  public isSaleFinishLoading$: Observable<boolean>
  public undecidedAmount: number = 0
  private prePaymentFactor: number = 0
  public isSaleCancelled: boolean = false

  constructor(
    private modalService: BsModalService,
    private store: Store<AppState>,
    private loadingModalService: LoadingModalService
  ) {}

  ngOnInit() {
    this.sale$ = this.store.select(saleSelectors.selectById, this.saleId)

    this.payments$ = this.store.select(paymentSelectors.selectAll).pipe(
      filter(() => !!this.saleId),
      distinctUntilChanged(
        (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
      ),
      map((payments) => {
        const salePayments = payments.filter(
          (payment) => payment.saleId === Number(this.saleId)
        )

        this.setRemainingAmountToPay(salePayments ?? [])
        this.shouldApplyPrePaymentFactor(salePayments ?? [])

        return salePayments
      })
    )

    this.canSetToPayInFrontend$ = combineLatest([
      this.sale$,
      this.payments$,
      this.store.select(
        configurationOptionSelectors.selectByKeyWithType<PayMethod[]>(
          ConfigOptionsKeys.ACTIVE_ALLOWED_PAY_METHODS
        )
      )
    ]).pipe(
      map(([sale, payments, allowedPayMethods]) => {
        if (!allowedPayMethods?.includes(PayMethod.CREDIT_CARD)) {
          return false
        }
        if (!sale || !payments?.length) {
          return false
        }
        return Boolean(
          sale.total > 0 &&
          ![StatusCode.PENDING_REQUIRED, StatusCode.CANCELLED].includes(
            sale.statusCode
          ) &&
          payments.find(
            (p) =>
              p.payMethod == PayMethodType.PAY_METHOD_UNDECIDED &&
              Number(p.amount) > 0
          )
        )
      })
    )

    this.isSaleFinishLoading$ = combineLatest([
      this.store.select(saleSelectors.selectIsLoading),
      this.store.select(paymentSelectors.selectIsLoading)
    ]).pipe(
      map(([saleLoading, paymentLoading]) => {
        this.loadingModalService.handle(saleLoading || paymentLoading)
        return saleLoading || paymentLoading
      })
    )
    this.modalValues$
      .pipe(
        filter(
          (p) =>
            p.payMethod === PayMethodType.PAY_METHOD_INVOICE &&
            p.collectStatus === CollectStatusType.COLLECT_STATUS_COLLECTABLE
        ),
        filter(Boolean),
        untilDestroyed(this)
      )
      .subscribe((salePayment: Payment) => {
        this.bsModalRef = this.modalService.show(SalePaymentModalComponent, {
          initialState: {
            salePayment
          }
        })
      })

    this.sale$.pipe(untilDestroyed(this)).subscribe((sale) => {
      this.isSaleCancelled = sale?.statusCode === StatusCode.CANCELLED
    })
  }

  public onClickCreatePayment() {
    const paymentAmount = this.prePaymentFactorActive
      ? this.undecidedAmount * this.prePaymentFactor
      : this.undecidedAmount
    this.sale$.pipe(take(1)).subscribe((sale) => {
      this.bsModalRef = this.modalService.show(PaymentCreateModalComponent, {
        initialState: {
          sale,
          undecidedAmount: Number(paymentAmount.toFixed(2))
        }
      })
    })
  }
  public onRowClicked(row: Payment) {
    this.modalValues$.next(row)
  }

  public onBulkAction(bulkActionEvent: BulkActionEvent) {
    if (bulkActionEvent.action == BulkAction.DELETE) {
      bulkActionEvent.items.forEach((i) => {
        this.store.dispatch(paymentActions.removeOnePartial({ id: i.id }))
      })
    }
  }

  public payInConsumerFrontend() {
    this.store.dispatch(
      saleActions.updateOneById({
        id: this.saleId,
        params: { statusCode: StatusCode.PENDING_REQUIRED }
      })
    )
  }

  ngOnDestroy() {
    this.loadingModalService.handle(false)
  }

  private setRemainingAmountToPay(payments: Payment[]): void {
    this.undecidedAmount = payments.reduce((total, payment) => {
      const isUndecidedCollectable =
        payment.payMethod === PayMethodType.PAY_METHOD_UNDECIDED &&
        payment.collectStatus === CollectStatusType.COLLECT_STATUS_COLLECTABLE

      if (!isUndecidedCollectable) {
        return total
      }

      return total + Number(payment.amount)
    }, 0)

    if (payments.length === 0) {
      this.sale$.pipe(take(1)).subscribe((sale) => {
        this.undecidedAmount = Number(sale?.total ?? 0)
        this.prePaymentFactorActive = false
        this.canCreatePayment = this.undecidedAmount > 0
      })
      return
    }

    this.canCreatePayment = this.undecidedAmount > 0
  }

  private shouldApplyPrePaymentFactor(payments: Payment[]): void {
    configOptionsSelector(this.store, ConfigOptionsKeys.PRE_PAYMENT_FACTOR)
      .pipe(take(1))
      .subscribe((prePaymentFactor) => {
        const prePaymentFactorValue = parseFloat(prePaymentFactor)
        const undecidedPayments = payments.filter(
          (payment) =>
            payment.payMethod === PayMethodType.PAY_METHOD_UNDECIDED &&
            payment.collectStatus ===
              CollectStatusType.COLLECT_STATUS_COLLECTABLE
        )
        const decidedPayments = payments.filter(
          (payment) => payment.payMethod !== PayMethodType.PAY_METHOD_UNDECIDED
        )
        const hasUndecidedPayments = undecidedPayments.length > 0
        const hasSingleUndecidedPayment =
          undecidedPayments.length === 1 &&
          Number(undecidedPayments[0].amount) > 0
        const hasDecidedPayments = decidedPayments.length > 0

        this.prePaymentFactorActive =
          hasUndecidedPayments &&
          hasSingleUndecidedPayment &&
          !hasDecidedPayments &&
          prePaymentFactorValue > 0 &&
          prePaymentFactorValue < 1
        this.prePaymentFactor = prePaymentFactorValue
      })
  }
}
